import React, { memo, useEffect } from 'react'
import { useSelector } from 'react-redux'

import {
    useStatisticsGetCompanyTotalConsumptionQuery,
    useStatisticsGetChargePointStatusStatsForCompanyQuery,
    useStatisticsGetCompanyUsageStatisticsQuery,
} from '../../../common/redux/api/web-api'
import { RootState } from '../../redux/store'
import { Header } from '../../../lib/header'
import Text from '../../../ui/Text'
import { PageLayoutContainer } from '../../../lib/page-layout-container'
import { BarChart, configureChart } from '../../../lib/bar-chart'
import { ChargePointStatusDashboardWidget } from '../../../lib/charge-point-status-dashboard-widget'
import { ChargingStatistics } from '../../../lib/charging-statistics'
import { WidgetLoadingState } from '../../../lib/widget-loading-state'

import { HeaderContainer, GraphContainer, LoadingTitle } from './dashboard-page.styling'

export const DashboardPage = memo(function DashboardPage(): React.ReactElement {
    const companyId = useSelector((state: RootState) => state.user.selectedCompanyId)

    const { data: totalConsumtionData, isLoading } = useStatisticsGetCompanyTotalConsumptionQuery({ companyId })
    const { data: chargePointStatistics } = useStatisticsGetChargePointStatusStatsForCompanyQuery({ companyId })
    const { data: companyStatistics } = useStatisticsGetCompanyUsageStatisticsQuery({ companyId })

    useEffect(() => {
        if (totalConsumtionData?.consumptionPerChargePoint?.length) {
            const data = totalConsumtionData.consumptionPerChargePoint
            const dyGraphCurrentData = data.map((item, index) => [index + 1, item.totalConsumptionKwh ?? 0])

            const xToIdMapper = data.reduce((map, item, index) => {
                return Object.assign(map, { [index + 1]: item.chargePointId ?? '' })
            }, {})

            configureChart(
                dyGraphCurrentData,
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                xToIdMapper,
                'lastSevenDaysGraph',
                100,
                80,
                true,
                'legendSevenDays',
                'kWh'
            )
        }
    }, [totalConsumtionData])

    return (
        <PageLayoutContainer>
            <div className="row">
                <div className="col-xs-12 col-md-10">
                    <div className="row">
                        {!isLoading && totalConsumtionData?.totalConsumption && (
                            <GraphContainer>
                                <HeaderContainer>
                                    <Header code="lblTotalConsumptionForMonth"></Header>
                                    <Text code="msgClickAndDrugToZoom" />
                                </HeaderContainer>
                                <BarChart
                                    barChartId="lastMonthGraph"
                                    data={totalConsumtionData?.totalConsumption}
                                    useMonth={false}
                                ></BarChart>
                            </GraphContainer>
                        )}
                        {isLoading && (
                            <GraphContainer>
                                <LoadingTitle className="shimmer-container shimmer-xs" size="md"></LoadingTitle>
                                <LoadingTitle className="shimmer-container shimmer-xs" size="sm"></LoadingTitle>
                                <WidgetLoadingState height="320px"></WidgetLoadingState>
                            </GraphContainer>
                        )}

                        {!isLoading &&
                            totalConsumtionData?.consumptionPerChargePoint &&
                            totalConsumtionData?.consumptionPerChargePoint?.length > 0 && (
                                <GraphContainer className="margin-top-xlarge">
                                    <HeaderContainer>
                                        <Header code="lblConsumptionPerCharger"></Header>
                                        <Text code="msgClickAndDrugToZoom" />
                                    </HeaderContainer>

                                    <div className="row">
                                        <div className="text-center">
                                            <div id="legendSevenDays" className="legend-one-month"></div>
                                        </div>
                                        <div
                                            id="lastSevenDaysGraph"
                                            className="admin-dashboard-graph"
                                            style={{ width: '95%' }}
                                            data-testid="bar-chart"
                                        ></div>
                                    </div>
                                </GraphContainer>
                            )}
                        {isLoading && (
                            <GraphContainer>
                                <LoadingTitle className="shimmer-container shimmer-xs" size="md"></LoadingTitle>
                                <LoadingTitle className="shimmer-container shimmer-xs" size="sm"></LoadingTitle>
                                <WidgetLoadingState height="320px"></WidgetLoadingState>
                            </GraphContainer>
                        )}
                    </div>
                </div>
                <div className="col-xs-12 col-md-2 stats-widget">
                    <ChargePointStatusDashboardWidget chargePointStatusStats={chargePointStatistics} />
                    <ChargingStatistics stats={companyStatistics} direction="column" />
                </div>
            </div>
        </PageLayoutContainer>
    )
})
